/**
 * AgentOsDemoReviewModerator.jsx — sandbox for Review Moderator Agent.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Sparkles, Loader2, ShieldAlert, ShieldCheck, ShieldX, Star } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const EXAMPLES = [
  { product_name: "Sandalwood hodinky Aurora", review_text: "Best ever! 100% amazing amazing wow wow! Best product ever bought best product ever bought.", rating: 5, reviewer_name: "Bot123", reviewer_account_age_days: 2, same_ip_other_reviews: 4 },
  { product_name: "Notebook ProBook 14", review_text: "Naozaj som rád, že som si tento notebook kúpil. Funguje rýchlo, klávesnica je pohodlná a batéria vydrží 8 hodín pri bežnom kancelárskom použití. Jediné mínus je trochu hlučnejší ventilátor pri záťaži.", rating: 4, reviewer_name: "Tomáš Vojtek", reviewer_account_age_days: 432, same_ip_other_reviews: 0 },
  { product_name: "Detox čaj 30-dňový", review_text: "SCAM! Nefunguje! Stratil som peniaze! Worst ever! Do not buy!", rating: 1, reviewer_name: "newuser_2026", reviewer_account_age_days: 1, same_ip_other_reviews: 2 },
];

export default function AgentOsDemoReviewModerator() {
  const [form, setForm] = useState({
    review_text: "", rating: 5, reviewer_name: "",
    reviewer_account_age_days: 365, same_ip_other_reviews: 0,
    product_name: "", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.review_text.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/review-moderator`, form);
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const verdictMap = {
    authentic: { Icon: ShieldCheck, color: "text-emerald-400", border: "border-emerald-700/40", bg: "bg-emerald-900/20", label: "Autentická recenzia" },
    suspicious: { Icon: ShieldAlert, color: "text-amber-400", border: "border-amber-700/40", bg: "bg-amber-900/20", label: "Podozrivá — manuálna kontrola" },
    fake: { Icon: ShieldX, color: "text-red-400", border: "border-red-700/40", bg: "bg-red-900/20", label: "Fake — odporúčam odstrániť" },
  };

  const Left = (
    <form onSubmit={submit} className="p-6 bg-black border border-gray-800" data-testid="review-moderator-form">
      <div className="flex gap-2 mb-5 flex-wrap">
        {EXAMPLES.map((_, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)} data-testid={`review-moderator-example-${i}`}
            className="text-[10px] px-3 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
            Príklad {i + 1}
          </button>
        ))}
      </div>
      <Field label="Produkt"><input value={form.product_name} maxLength={200}
        onChange={(e) => setForm({ ...form, product_name: e.target.value })}
        data-testid="review-moderator-input-product"
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      <Field label="Text recenzie"><textarea required rows={5} value={form.review_text} maxLength={4000}
        onChange={(e) => setForm({ ...form, review_text: e.target.value })}
        data-testid="review-moderator-input-text"
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none resize-none" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Hodnotenie">
          <div className="flex gap-1 px-2 py-2 bg-neutral-950 border border-gray-800" data-testid="review-moderator-input-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}
                className={n <= form.rating ? "text-amber-400" : "text-gray-700"}>
                <Star className="w-5 h-5" fill={n <= form.rating ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
        </Field>
        <Field label="Meno recenzenta"><input value={form.reviewer_name} maxLength={120}
          onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })}
          data-testid="review-moderator-input-reviewer"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Vek účtu (dni)"><input type="number" min="0" value={form.reviewer_account_age_days}
          onChange={(e) => setForm({ ...form, reviewer_account_age_days: parseInt(e.target.value) || 0 })}
          data-testid="review-moderator-input-age"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
        <Field label="Iné recenzie z IP"><input type="number" min="0" value={form.same_ip_other_reviews}
          onChange={(e) => setForm({ ...form, same_ip_other_reviews: parseInt(e.target.value) || 0 })}
          data-testid="review-moderator-input-ip"
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" /></Field>
      </div>
      <button type="submit" disabled={status.loading || !form.review_text}
        data-testid="review-moderator-submit"
        className="w-full mt-2 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:bg-gray-700 disabled:text-gray-400 text-black font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2">
        {status.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzujem…</> : <><Sparkles className="w-4 h-4" /> Vyhodnoť recenziu</>}
      </button>
    </form>
  );

  const Right = (
    <div className="p-6 bg-black border border-gray-800" data-testid="review-moderator-output">
      {!result && !status.loading && !status.error && <Placeholder Icon={ShieldCheck} text="AI vyhodnotí autenticitu recenzie a navrhne akciu" />}
      {status.loading && <Loading text="Analyzujem fake patterns…" />}
      {status.error && <ErrorBox err={status.error} />}
      {result?.output && (() => {
        const v = verdictMap[result.output.verdict] || verdictMap.suspicious;
        const V = v.Icon;
        return (
          <div className="space-y-4 text-sm">
            <div className={`flex items-center gap-4 p-4 ${v.bg} border ${v.border}`}>
              <V className={`w-7 h-7 ${v.color}`} />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Verdikt</p>
                <p className={`text-lg font-black ${v.color}`} data-testid="review-moderator-output-verdict">{v.label}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Risk score</p>
                <p className={`text-2xl font-black ${v.color} tabular-nums`} data-testid="review-moderator-output-score">{result.output.risk_score}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Suggested action" value={result.output.suggested_action} />
              <Stat label="Naturalness" value={`${result.output.naturalness_score}/100`} />
            </div>
            {result.output.evidence?.length > 0 && (
              <div className="pt-3 border-t border-gray-900">
                <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Heuristic evidence</p>
                <ul className="space-y-1">{result.output.evidence.map((e, i) => <li key={i} className="text-xs text-gray-300">• {e}</li>)}</ul>
              </div>
            )}
            {result.output.language_flags?.length > 0 && (
              <div className="pt-3 border-t border-gray-900">
                <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">LLM language flags</p>
                <ul className="space-y-1">{result.output.language_flags.map((f, i) => <li key={i} className="text-xs text-gray-300">• {f}</li>)}</ul>
              </div>
            )}
            <p className="text-[10px] text-gray-600 pt-3 border-t border-gray-900">Zostáva {result.remaining_today} demo runov. Latencia: {result.latency_ms}ms.</p>
          </div>
        );
      })()}
    </div>
  );

  return <AgentOsDemoShell testid="agent-os-demo-review-moderator" h1="Review Moderator" lead="Detekcia fake recenzií + competitor sabotage. Heuristic + LLM dvojvrstvová analýza." left={Left} right={Right} />;
}

const Stat = ({ label, value }) => (<div className="p-2 bg-neutral-950 border border-gray-900"><p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{label}</p><p className="text-sm font-black text-emerald-300">{value}</p></div>);
const Field = ({ label, children }) => (<label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>{children}</label>);
const Placeholder = ({ Icon, text }) => (<div className="text-center py-10"><Icon className="w-10 h-10 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-500">{text}</p></div>);
const Loading = ({ text }) => (<div className="text-center py-10"><Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" /><p className="text-sm text-gray-400">{text}</p></div>);
const ErrorBox = ({ err }) => (<div className="p-4 border border-red-900/60 bg-red-950/30 text-sm text-red-300" data-testid="review-moderator-error">{err.message || "Skús to znova."}</div>);
